import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Grid,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  Button,
} from "@mui/material";
import { useReactToPrint } from "react-to-print";
import http from "../../../commonFunctions/httpClient";
import WSButton from "../../../Components/Button/WSButton";
import logo from "../../../images/logo_invoice.svg";

interface LabUser {
  userId: string;
  name: string;
  gender: string;
  age: number;
  profileImage: string;
  status: string | null;
  bookingDate: string;
}

interface LabTest {
  userId: string;
  userName: string;
  testName: string;
}

interface LabBookingInfo {
  bookingId: string;
  bookedOn: string;
  lab: {
    labId: string;
    labName: string;
  };
  payment: {
    method: string;
    amount: number;
  };
  tests: LabTest[];
  slot: {
    date: string;
    time: string;
  };
  collection: {
    type: string;
    address: {
      line1: string;
      landmark: string;
      city: string | null;
      district: string | null;
      pincode: string;
    };
  };
}

interface OrderTracking {
  orderId: string;
  trackingId: string | null;
  steps: Array<{ status: string }>;
}

interface LabResult {
  userId: string;
  userName: string;
  resultAvailable: boolean;
  reportPdfUrl: string;
}

interface OrderSummary {
  itemTotal: {
    quantity: number;
    pricePerItem: number;
    total: number;
  };
  discount: number;
  homeCollectionFee: number;
  serviceFeeAndTax: number;
  totalPayable: number;
  savings: number;
}

interface LabBookingResponse {
  success: boolean;
  message: string;
  data: {
    users: LabUser[];
    testBookingInfo: LabBookingInfo[];
    orderTracking: OrderTracking[];
    results: LabResult[];
    orderSummary: OrderSummary[];
  };
  pagination: any;
}

interface LabInvoiceReportProps {
  orderId: string;
}

export const LabInvoiceReport: React.FC<LabInvoiceReportProps> = ({ orderId }) => {
  const [labData, setLabData] = useState<LabBookingResponse['data'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const contentRef = useRef<HTMLDivElement>(null);

  const fetchLabData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      
      console.log("🔄 Fetching lab order for orderId:", orderId);
      
      const response: LabBookingResponse = await http.get(
        `lab/LabBooking/details/${orderId}`
      );
      
      console.log("📊 FULL LAB ORDER API RESPONSE:", response);
      
      if (response.success) {
        setLabData(response.data);
      } else {
        setError("No valid lab order found for this order ID");
        setLabData(null);
      }
      
    } catch (error: any) {
      console.error("❌ LAB ORDER API ERROR:", error);
      setError("Failed to fetch lab order data");
      setLabData(null);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    if (orderId) {
      fetchLabData();
    }
  }, [fetchLabData]);

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "Invalid Date";
    }
  };

  const formatTimeSlot = (timeSlot: string) => {
    return timeSlot;
  };

  const formatCurrency = (amount: number) => {
    return `₹${amount.toLocaleString("en-IN")}`;
  };

  const handlePrint = useReactToPrint({
    contentRef
  });

  if (loading) {
    return (
      <Box p={4} display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading lab invoice...</Typography>
      </Box>
    );
  }

  if (error || !labData) {
    return (
      <Box p={4}>
        <Alert severity="error">
          {error || "No lab order data found for this order ID"}
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Order ID: {orderId}
            </Typography>
          </Box>
        </Alert>
      </Box>
    );
  }

  const firstBooking = labData.testBookingInfo[0] || {};
  const firstUser = labData.users[0] || {};
  const firstSummary = labData.orderSummary[0] || {};
  const firstTracking = labData.orderTracking[0] || {};
  const allTests = firstBooking.tests || [];

  return (
    <Box p={4} sx={{ backgroundColor: "#fff", maxWidth: "100%" }}>
      <Box sx={{ mb: 3, textAlign: "center" }}>
        <WSButton
          id="printLabInvoiceBtn"
          size="small"
          label={`Print Lab Order #${orderId.slice(-6).toUpperCase()}`}
          onClick={handlePrint}
        />
      </Box>

      <Box
        ref={contentRef}
        sx={{ 
          p: 4, 
          width: "100%", 
          maxWidth: "800px", 
          margin: "0 auto",
          bgcolor: "white",
          '@media print': {
            maxWidth: "none",
            width: "100%",
            p: 2,
            margin: 0
          }
        }}
      >
        {/* HEADER */}
        <Grid container sx={{ mb: 3 }}>
          {/* LOGO - TOP CENTER */}
          <Grid item xs={12} textAlign="left" sx={{ mb: -4 }}>
            <Box
              component="img"
              src={logo}
              alt="MELP Lab Logo"
              sx={{
                width: 100,
                height: 100,
                borderRadius: "12px",
                objectFit: "contain",
                '@media print': {
                  filter: "none",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
                }
              }}
              onError={(e: any) => {
                e.currentTarget.style.display = "none";
              }}
            />
          </Grid>
          
          {/* BOTTOM ROW - MELP Lab LEFT | EMPTY RIGHT */}
          <Grid item xs={12}>
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={6}>
                <Typography fontSize={20} fontWeight="bold" sx={{ color: "#098963", mb: 1 }}>
                  MELP Lab
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                  Medical Excellence Lab Services
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  123 Health Street, Kerala - 670001
                </Typography>
                <Typography variant="body2">
                  Phone: +91 98765 43210
                </Typography>
              </Grid>
              <Grid item xs={6} sx={{ minHeight: 80 }} />
            </Grid>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* ORDER DETAILS */}
        <Grid container justifyContent="space-between" mb={3}>
          <Grid item xs={6}>
            <Typography fontSize={18} gutterBottom sx={{ fontWeight: 700 }}>
              Lab Order #{orderId.slice(-6).toUpperCase()}
            </Typography>
            <Typography sx={{ fontWeight: 500, mb: 1 }}>
              <strong>Patient:</strong> {firstUser.name}
            </Typography>
            <Typography sx={{ mb: 0.5 }}>
              <strong>Gender/Age:</strong> {firstUser.gender}, {firstUser.age} yrs
            </Typography>
            <Typography sx={{ mb: 0.5 }}>
              <strong>Collection:</strong> {firstBooking.collection?.type || 'N/A'} Collection
            </Typography>
            {firstBooking.collection?.type === "Home" && (
              <>
                <Typography sx={{ mb: 0.5 }}>
                  <strong>Address:</strong> {firstBooking.collection.address.line1}, {firstBooking.collection.address.landmark}
                </Typography>
                <Typography>
                  <strong>Pincode:</strong> {firstBooking.collection.address.pincode}
                </Typography>
              </>
            )}
          </Grid>
          <Grid item xs={6} textAlign="right">
            <Typography fontSize={18} sx={{ fontWeight: 700, mb: 2 }}>
              <strong>Date:</strong> {formatDate(firstBooking.bookedOn)}
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Slot:</strong> {formatDate(firstBooking.slot.date)} {formatTimeSlot(firstBooking.slot.time)}
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Payment:</strong> {firstBooking.payment?.method || 'N/A'}
            </Typography>
            {firstTracking.steps.length > 0 && (
              <Chip
                label={firstTracking.steps[firstTracking.steps.length - 1].status}
                color={firstTracking.steps[firstTracking.steps.length - 1].status === "Completed" ? "success" : "default"}
                sx={{ fontWeight: 600 }}
              />
            )}
          </Grid>
        </Grid>

        {/* TESTS TABLE */}
        <TableContainer sx={{ mb: 4, borderRadius: 2, boxShadow: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#098963" }}>
                <TableCell sx={{ fontWeight: 700, color: 'white' }}>Test Name</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'white' }} align="center">Patient</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'white' }} align="right">Price</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'white' }} align="center">Qty</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allTests.length > 0 ? (
                allTests.map((test: LabTest, index: number) => (
                  <TableRow key={test.userId + index} sx={{ '&:hover': { bgcolor: 'grey.50' } }}>
                    <TableCell sx={{ fontWeight: 500 }}>
                      <Typography variant="body2">{test.testName}</Typography>
                    </TableCell>
                    <TableCell align="center">{test.userName}</TableCell>
                    <TableCell align="right">
                      {formatCurrency(firstSummary.itemTotal?.pricePerItem || 0)}
                    </TableCell>
                    <TableCell align="center">1</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No tests found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* BILL SUMMARY */}
        <Box sx={{ borderTop: "3px solid #098963", pt: 3, mb: 4 }}>
          <Grid container justifyContent="flex-end">
            <Grid item xs={12} sm={6} md={4}>
              <Box textAlign="right">
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  <strong>Items Total:</strong> {formatCurrency(firstSummary.itemTotal?.total || 0)}
                </Typography>
                {firstSummary.discount > 0 && (
                  <Typography variant="h6" color="error" sx={{ fontWeight: 700, mb: 1 }}>
                    <strong>Discount:</strong> -{formatCurrency(firstSummary.discount)}
                  </Typography>
                )}
                {firstSummary.homeCollectionFee > 0 && (
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    <strong>Home Collection:</strong> {formatCurrency(firstSummary.homeCollectionFee)}
                  </Typography>
                )}
                {firstSummary.serviceFeeAndTax > 0 && (
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                    <strong>Service Fee & Tax:</strong> {formatCurrency(firstSummary.serviceFeeAndTax)}
                  </Typography>
                )}
                <Divider sx={{ my: 1, borderWidth: 2 }} />
                <Typography fontSize={20} color="#098963" sx={{ fontWeight: 900, mb: 1 }}>
                  <strong>Total Payable:</strong> {formatCurrency(firstSummary.totalPayable)}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* RESULTS STATUS */}
        {labData.results.length > 0 && (
          <>
            <Divider sx={{ my: 4 }} />
            <Grid container justifyContent="center">
              <Grid item xs={12} textAlign="center">
                <Typography variant="h6" sx={{ mb: 1, color: labData.results[0].resultAvailable ? "success.main" : "warning.main" }}>
                  {labData.results[0].resultAvailable ? "✅ Results Available" : "⏳ Results Pending"}
                </Typography>
                {labData.results[0].resultAvailable && labData.results[0].reportPdfUrl && (
                  <Typography variant="body2" sx={{ color: "primary.main" }}>
                    Report ready for download
                  </Typography>
                )}
              </Grid>
            </Grid>
          </>
        )}

        {/* FOOTER */}
        <Divider sx={{ my: 4 }} />
        <Box textAlign="center">
          <Typography variant="h5" color="#098963" sx={{ fontWeight: 700, mb: 2 }}>
            🧪 Accurate. Reliable. Fast. 💙
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Thank you for choosing MELP Lab | Kerala
          </Typography>
          <Typography sx={{ 
            fontSize: '0.8rem', 
            color: 'text.secondary', 
            mt: 2,
            '@media print': { fontSize: '0.9rem' }
          }}>
            * This is a computer-generated lab invoice
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
